import { Navigate, useOutletContext } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/api/axios';

/**
 * Route-level permission guard.
 *
 * Wraps a page and verifies that the *current* user has access to the given
 * module FOR the currently-selected business. Without this, hiding items from
 * the sidebar is not enough — users could still type the URL manually.
 *
 *   module:
 *     - 'dashboard' | 'customers' | 'orders' | 'templates' | 'catalogo'
 *       | 'reports' | 'settings'
 *       → checked against user_permissions[selectedBusiness] for vendors,
 *         always allowed for admin.
 *     - 'inbox'          → 'inbox' OR 'inbox_asignado'
 *     - 'ranking' | 'vendors' | 'coverage' → admin OR supervisor only.
 *     - 'admin'          → admin only.
 */
const PermissionGuard = ({ module, children }) => {
  const { user } = useAuth();
  const ctx = useOutletContext() || {};
  const selectedBusiness = ctx.selectedBusiness;

  const [permissions, setPermissions] = useState(null); // null = loading
  const [denied, setDenied] = useState([]);
  const isAdmin = user?.role === 'admin';
  const isSupervisor = user?.role === 'supervisor';

  useEffect(() => {
    let cancelled = false;
    // Admin bypasses default modules but still respects denied_modules
    if (!selectedBusiness?.id) {
      // Even without business_id we may have denied_modules — fetch using "global"
      api.get(`/users/me/permissions/global`).then((res) => {
        if (!cancelled) {
          setPermissions(isAdmin ? ['*'] : (res.data.permissions || []));
          setDenied(res.data.denied_modules || []);
        }
      }).catch(() => { if (!cancelled) { setPermissions(isAdmin ? ['*'] : []); setDenied([]); } });
      return () => { cancelled = true; };
    }
    api
      .get(`/users/me/permissions/${selectedBusiness.id}`)
      .then((res) => {
        if (!cancelled) {
          setPermissions(isAdmin ? ['*'] : (res.data.permissions || []));
          setDenied(res.data.denied_modules || []);
        }
      })
      .catch(() => {
        if (!cancelled) { setPermissions(isAdmin ? ['*'] : []); setDenied([]); }
      });
    return () => {
      cancelled = true;
    };
  }, [isAdmin, selectedBusiness?.id]);

  // Loading state
  if (permissions === null) {
    return (
      <div className="h-full flex items-center justify-center bg-[var(--bg)]">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-500 border-t-transparent"></div>
      </div>
    );
  }

  // Explicit denylist always wins (overrides default-allowed modules)
  if (denied.includes(module)) return <RouteFallback />;

  // Admins bypass everything else
  if (isAdmin) return children;

  if (module === 'mi_dia') return children;
  if (module === 'pendientes') return children;
  if (module === 'chat_interno') return children; // Internal chat — available to all
  if (module === 'cotizador') return children; // available to all authenticated users
  if (module === 'stock') return children;
  // Asistente IA: solo Admin/Supervisor/Gerente
  if (module === 'asistente_ia') {
    if (['admin', 'supervisor', 'gerente'].includes((user?.role || '').toLowerCase())) return children;
    return <RouteFallback />;
  }
  if (module === 'clientes_vendedores') return children; // every authenticated user sees their own
  if (module === 'clientes') return children; // ficha maestra de clientes — visible a todos (vendedor asignado protegido en backend)
  // Producción — roles orientados a producción o permiso explícito (igual que el menú)
  if (module === 'produccion') {
    if (['admin', 'supervisor', 'gerente', 'produccion', 'operaciones', 'financiero'].includes((user?.role || '').toLowerCase())) return children;
    if (permissions.includes('produccion')) return children;
    return <RouteFallback />;
  }
  // Instaladores — roles de producción/supervisión o permiso explícito
  if (module === 'instaladores') {
    if (['admin', 'supervisor', 'gerente', 'produccion', 'operaciones'].includes((user?.role || '').toLowerCase())) return children;
    if (permissions.includes('instaladores')) return children;
    return <RouteFallback />;
  }
  // Leads Landing — solo admin (bypass arriba) o permiso explícito
  if (module === 'leads_landing') {
    if (permissions.includes('leads_landing')) return children;
    return <RouteFallback />;
  }
  // Role-based modules (now also allow explicit per-business permissions)
  if (['ranking', 'vendors', 'coverage', 'plan_trabajo', 'reportes', 'reports'].includes(module)) {
    if (isSupervisor) return children;
    if (permissions.includes(module)) return children;
    return <RouteFallback />;
  }
  if (module === 'admin') return <RouteFallback />;

  // Inbox accepts either 'inbox' or 'inbox_asignado'
  if (module === 'inbox') {
    if (permissions.includes('inbox') || permissions.includes('inbox_asignado'))
      return children;
    return <RouteFallback />;
  }

  // Default: explicit permission per business
  if (permissions.includes(module)) return children;
  return <RouteFallback />;
};

/**
 * When a user lands on a page they cannot access, send them to the first
 * route they actually have access to. If we can't determine that yet,
 * just redirect to /inbox (the universal fallback for vendors).
 */
const RouteFallback = () => {
  return <Navigate to="/inbox" replace />;
};

export default PermissionGuard;
